// frontend/src/pages/AdminRoleTypes.js
import React, { useEffect, useState } from 'react';
import { getRoleTypes, createRoleType, updateRoleType, deleteRoleType, getDepartments } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useActiveDepartment } from '../context/DepartmentContext';
import { Card, Badge, Btn, Modal, Field, Input, Select, Spinner } from '../components/UI';
import PageHeader, { ViewOnlyNotice } from '../components/PageHeader';

const BADGE_COLORS = ['blue','green','amber','red','gray'];

export default function AdminRoleTypes() {
  const { user } = useAuth();
  const { activeDepartmentId } = useActiveDepartment();
  const isAdmin    = user?.role === 'admin';
  const isDeptHead = user?.role === 'dept_head';
  const canManageHere = isAdmin || (isDeptHead && activeDepartmentId != null && Number(activeDepartmentId) === Number(user?.department_id));
  const [roles,   setRoles]   = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm,setShowForm]= useState(false);
  const [editing, setEditing] = useState(null);
  const [saving,  setSaving]  = useState(false);

  const emptyForm = { key:'', label:'', badge_color:'blue', bg_color:'#E6F1FB', text_color:'#185FA5', quarterly_reward:0, can_be_champion:0, sort_order:'', department_id:'' };
  const [form, setForm] = useState(emptyForm);

  const load = () => getRoleTypes().then(d => setRoles(Array.isArray(d) ? d : [])).catch(console.error);
  useEffect(() => {
    load().finally(() => setLoading(false));
    getDepartments().then(d => setDepartments(Array.isArray(d) ? d : [])).catch(() => setDepartments([]));
  }, []);

  const deptName = id => departments.find(d => String(d.id) === String(id))?.name || 'Program-wide';

  const openCreate = () => { setEditing(null); setForm({ ...emptyForm, department_id: activeDepartmentId ? String(activeDepartmentId) : '' }); setShowForm(true); };
  const openEdit = r => {
    setEditing(r);
    setForm({
      key: r.key, label: r.label, badge_color: r.badge_color || 'gray',
      bg_color: r.bg_color || '#F3F4F6', text_color: r.text_color || '#374151',
      quarterly_reward: r.quarterly_reward || 0, can_be_champion: Number(r.can_be_champion) || 0,
      sort_order: r.sort_order || '', department_id: r.department_id ? String(r.department_id) : '',
    });
    setShowForm(true);
  };

  const handleSubmit = async e => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing) await updateRoleType(editing.id, form);
      else await createRoleType(form);
      await load(); setShowForm(false);
    } catch (err) { alert(err.response?.data?.error || 'Error saving role type'); }
    finally { setSaving(false); }
  };

  const handleDelete = async r => {
    if (!window.confirm(`Delete role type "${r.label}"? This will fail if any users or templates still use it.`)) return;
    try { await deleteRoleType(r.id); await load(); }
    catch (err) { alert(err.response?.data?.error || 'Error deleting'); }
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: '20px clamp(16px, 3vw, 32px)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <PageHeader moduleKey="role-types" helpTask="define-role-types" />
        {canManageHere && <Btn size="sm" onClick={openCreate}>+ Add role type</Btn>}
      </div>

      {!canManageHere && <ViewOnlyNotice />}

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--gray-50)' }}>
              {['Order','Key','Label','Badge','Quarterly reward','Champion eligible','Department','Actions'].map(h => (
                <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-3)', borderBottom: '0.5px solid var(--border)', fontWeight: 500 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {roles.map(r => (
              <tr key={r.id} style={{ borderBottom: '0.5px solid var(--border)' }}>
                <td style={{ padding: '12px 16px', color: 'var(--text-3)' }}>{r.sort_order}</td>
                <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontSize: 12 }}>{r.key}</td>
                <td style={{ padding: '12px 16px', fontWeight: 500 }}>{r.label}</td>
                <td style={{ padding: '12px 16px' }}>
                  <Badge color={r.badge_color}>{r.label}</Badge>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  {Number(r.quarterly_reward) > 0 ? `PHP ${Number(r.quarterly_reward).toLocaleString()}` : '—'}
                </td>
                <td style={{ padding: '12px 16px' }}>
                  {Number(r.can_be_champion) ? <Badge color="green">Yes</Badge> : <span style={{ color: 'var(--text-3)' }}>No</span>}
                </td>
                <td style={{ padding: '12px 16px', color: 'var(--text-2)' }}>{deptName(r.department_id)}</td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {canManageHere ? (<>
                      <Btn variant="secondary" size="sm" onClick={() => openEdit(r)}>Edit</Btn>
                      <Btn variant="ghost" size="sm" onClick={() => handleDelete(r)} style={{ color: 'var(--red-500)' }}>Delete</Btn>
                    </>) : <span style={{ color: 'var(--text-3)', fontSize: 12 }}>View only</span>}
                  </div>
                </td>
              </tr>
            ))}
            {roles.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: 'var(--text-3)' }}>No role types defined. Click "+ Add role type" to create one.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <Modal open={showForm} onClose={() => setShowForm(false)} title={editing ? `Edit — ${editing.label}` : 'New role type'} width={480}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Key (stored value)" required>
              <Input value={form.key} onChange={e => setForm(f => ({ ...f, key: e.target.value }))} placeholder="e.g. TechLead" disabled={!!editing} />
            </Field>
            <Field label="Display label" required>
              <Input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} placeholder="e.g. Tech Lead" />
            </Field>
          </div>
          <Field label="Department">
            <Select value={form.department_id} disabled={isDeptHead}
              onChange={e => setForm(f => ({ ...f, department_id: e.target.value }))}>
              <option value="">Program-wide (all departments)</option>
              {departments.map(d => (
                <option key={d.id} value={d.id}>
                  {d.name}{d.program_name ? ` · ${d.program_name}` : ''}
                </option>
              ))}
            </Select>
            {editing && isAdmin && (
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
                Moving is only allowed while no users or templates use this role in its current department.
              </div>
            )}
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Field label="Badge color">
              <Select value={form.badge_color} onChange={e => setForm(f => ({ ...f, badge_color: e.target.value }))}>
                {BADGE_COLORS.map(c => <option key={c} value={c}>{c}</option>)}
              </Select>
            </Field>
            <Field label="Background color">
              <Input type="color" value={form.bg_color} onChange={e => setForm(f => ({ ...f, bg_color: e.target.value }))} style={{ padding: 2, height: 36 }} />
            </Field>
            <Field label="Text color">
              <Input type="color" value={form.text_color} onChange={e => setForm(f => ({ ...f, text_color: e.target.value }))} style={{ padding: 2, height: 36 }} />
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Field label="Quarterly reward (PHP)">
              <Input type="number" min="0" step="500" value={form.quarterly_reward} onChange={e => setForm(f => ({ ...f, quarterly_reward: e.target.value }))} />
            </Field>
            <Field label="Champion eligible">
              <Select value={form.can_be_champion} onChange={e => setForm(f => ({ ...f, can_be_champion: Number(e.target.value) }))}>
                <option value={0}>No</option>
                <option value={1}>Yes</option>
              </Select>
            </Field>
            <Field label="Sort order">
              <Input type="number" min="0" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))} placeholder="Auto" />
            </Field>
          </div>
          <div style={{ marginTop: 8, padding: '8px 12px', background: 'var(--gray-50)', borderRadius: 8, fontSize: 12 }}>
            Preview: <Badge color={form.badge_color}>{form.label || 'Label'}</Badge>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <Btn variant="secondary" onClick={() => setShowForm(false)}>Cancel</Btn>
            <Btn disabled={saving || !form.key || !form.label}>{saving ? 'Saving...' : editing ? 'Save changes' : 'Create'}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
